import React from 'react';
import {
  Drawer,
  SwipeableDrawer,
  Box,
  Typography,
  Stack,
  Chip,
  IconButton,
  Button,
  Divider,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import LanguageIcon from '@mui/icons-material/Language';
import PhoneIcon from '@mui/icons-material/Phone';
import StarIcon from '@mui/icons-material/Star';
import StarBorderIcon from '@mui/icons-material/StarBorder';
import BoltIcon from '@mui/icons-material/Bolt';
import CropFreeIcon from '@mui/icons-material/CropFree';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import ShareIcon from '@mui/icons-material/Share';
import { getCategoryColor } from './categoryPalette';

const VendorCard = ({
  cell,
  open,
  onClose,
  onShowOnMap,
  isFavorite,
  onToggleFavorite,
  onShare,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const Container = isMobile ? SwipeableDrawer : Drawer;
  const containerProps = isMobile
    ? {
        anchor: 'bottom',
        onOpen: () => {},
        disableSwipeToOpen: true,
        PaperProps: {
          sx: {
            borderTopLeftRadius: 16,
            borderTopRightRadius: 16,
            maxHeight: '85vh',
          },
        },
      }
    : {
        anchor: 'right',
        PaperProps: {
          sx: { width: 400, maxWidth: '100vw' },
        },
      };

  if (!cell) return null;

  const categoryColor = getCategoryColor(cell.category_slug);
  const categoryLabel = cell.category_name
    || (cell.category_other_description ? `Other — ${cell.category_other_description}` : null);

  return (
    <Container open={open} onClose={onClose} {...containerProps}>
      {isMobile && (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            pt: 1,
          }}
        >
          <Box sx={{ width: 36, height: 4, borderRadius: 2, background: '#ddd' }} />
        </Box>
      )}
      <Box sx={{ p: 3, pb: 4, position: 'relative' }}>
        <IconButton
          onClick={onClose}
          aria-label="close"
          sx={{ position: 'absolute', top: 8, right: 8 }}
        >
          <CloseIcon />
        </IconButton>

        <Box sx={{ pr: 4 }}>
          <Typography variant="h5" sx={{ fontWeight: 700, mb: 0.5 }}>
            {cell.vendor_name || 'Unassigned booth'}
          </Typography>
          {categoryLabel && (
            <Chip
              size="small"
              label={categoryLabel}
              sx={{
                background: categoryColor,
                color: '#fff',
                fontWeight: 600,
                mb: 1.5,
              }}
            />
          )}

          <Stack direction="row" spacing={0.75} sx={{ mb: 2, flexWrap: 'wrap', gap: 0.75 }}>
            <Chip
              size="small"
              icon={<LocationOnIcon fontSize="small" />}
              label={`Booth ${cell.cell_name}`}
              variant="outlined"
            />
            {cell.section && (
              <Chip
                size="small"
                label={`Section ${cell.section}`}
                variant="outlined"
              />
            )}
            {cell.is_corner && (
              <Chip
                size="small"
                icon={<CropFreeIcon fontSize="small" />}
                label="Corner"
                color="warning"
                variant="outlined"
              />
            )}
            {cell.has_electricity && (
              <Chip
                size="small"
                icon={<BoltIcon fontSize="small" />}
                label="Electricity"
                color="info"
                variant="outlined"
              />
            )}
          </Stack>
        </Box>

        {cell.vendor_description && (
          <>
            <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2, whiteSpace: 'pre-line' }}>
              {cell.vendor_description}
            </Typography>
            <Divider sx={{ mb: 2 }} />
          </>
        )}

        <Stack spacing={1.25} sx={{ mb: 3 }}>
          {cell.vendor_phone && (
            <Stack direction="row" spacing={1.5} alignItems="center">
              <PhoneIcon fontSize="small" color="action" />
              <Typography
                variant="body2"
                component="a"
                href={`tel:${cell.vendor_phone}`}
                sx={{ color: 'primary.main', textDecoration: 'none' }}
              >
                {cell.vendor_phone}
              </Typography>
            </Stack>
          )}
          {cell.vendor_website && (
            <Stack direction="row" spacing={1.5} alignItems="center">
              <LanguageIcon fontSize="small" color="action" />
              <Typography
                variant="body2"
                component="a"
                href={normalizeUrl(cell.vendor_website)}
                target="_blank"
                rel="noopener noreferrer"
                sx={{ color: 'primary.main', textDecoration: 'none', wordBreak: 'break-all' }}
              >
                {cell.vendor_website}
              </Typography>
            </Stack>
          )}
        </Stack>

        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
          <Button
            fullWidth
            variant="contained"
            startIcon={<LocationOnIcon />}
            onClick={onShowOnMap}
          >
            Show on map
          </Button>
          <Button
            variant={isFavorite ? 'contained' : 'outlined'}
            color="warning"
            startIcon={isFavorite ? <StarIcon /> : <StarBorderIcon />}
            onClick={onToggleFavorite}
            sx={{ minWidth: 140 }}
          >
            {isFavorite ? 'Saved' : 'Save'}
          </Button>
          {onShare && (
            <IconButton onClick={onShare} aria-label="share booth" sx={{ alignSelf: 'center' }}>
              <ShareIcon />
            </IconButton>
          )}
        </Stack>
      </Box>
    </Container>
  );
};

const normalizeUrl = (url) => {
  if (!url) return '#';
  if (/^https?:\/\//i.test(url)) return url;
  return `https://${url}`;
};

export default VendorCard;
